import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { WA_PRIMARY_LABEL, WA_SECONDARY_LABEL, DIRECT_MSG, waLink } from "@/data/site";
import { useSiteSettings, formatPhoneDisplay } from "@/lib/settings";

export default function FloatingWhatsApp() {
    const [open, setOpen] = useState(false);
    const settings = useSiteSettings();

    return (
        <div
            className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 sm:bottom-7 sm:right-7"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
            data-testid="floating-whatsapp-widget"
        >
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.95 }}
                        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                        className="w-72 rounded-3xl border border-[#E5DEC9] bg-white p-5 shadow-2xl shadow-forest-deep/25"
                        data-testid="floating-whatsapp-panel"
                    >
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-wa opacity-75" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-wa" />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-wa-dark">Online agora</span>
                        </div>
                        <p className="mt-2 font-serif text-lg font-semibold text-forest-deep">Fale com nossos especialistas em Resorts</p>
                        <p className="mt-0.5 text-xs text-stone-500">Cotações rápidas, todos os dias.</p>
                        <div className="mt-4 space-y-2.5">
                            <a
                                data-testid="whatsapp-btn-primary"
                                href={waLink(settings.whatsapp_primary, DIRECT_MSG)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 rounded-2xl bg-wa px-4 py-3.5 text-white transition-all duration-200 hover:bg-wa-dark active:scale-[0.98]"
                            >
                                <MessageCircle className="h-5 w-5 shrink-0" />
                                <span>
                                    <span className="block text-sm font-semibold">{formatPhoneDisplay(settings.whatsapp_primary)}</span>
                                    <span className="block text-[11px] text-white/85">{WA_PRIMARY_LABEL}</span>
                                </span>
                            </a>
                            <a
                                data-testid="whatsapp-btn-secondary"
                                href={waLink(settings.whatsapp_secondary, DIRECT_MSG)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 rounded-2xl border-2 border-wa/60 px-4 py-3.5 text-forest-deep transition-all duration-200 hover:border-wa hover:bg-wa/5 active:scale-[0.98]"
                            >
                                <MessageCircle className="h-5 w-5 shrink-0 text-wa-dark" />
                                <span>
                                    <span className="block text-sm font-semibold">{formatPhoneDisplay(settings.whatsapp_secondary)}</span>
                                    <span className="block text-[11px] text-stone-500">{WA_SECONDARY_LABEL}</span>
                                </span>
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                type="button"
                data-testid="whatsapp-floating-toggle"
                aria-label={open ? "Fechar contatos de WhatsApp" : "Abrir contatos de WhatsApp"}
                onClick={() => setOpen((v) => !v)}
                whileTap={{ scale: 0.9 }}
                className={`wa-pulse flex h-12 w-12 items-center justify-center rounded-full text-white shadow-2xl shadow-emerald-900/40 transition-colors duration-300 sm:h-14 sm:w-14 ${
                    open ? "bg-forest-deep" : "bg-wa hover:bg-wa-dark"
                }`}
            >
                {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
            </motion.button>
        </div>
    );
}
